import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  makeStyles,
  CircularProgress,
  Button
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: '100%',
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  contactInfo: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
}));

const CustomerComplaintForm = ({
  clients,
  preSelectedClient,
  preSelectedJobsite,
  onSubmit,
  loading,
  url,
}) => {
  const classes = useStyles();
  
  const [formData, setFormData] = useState({
    clientId: preSelectedClient?._id || '',
    jobsiteId: preSelectedJobsite?._id || '',
    complaintType: '',
    priority: 'medium',
    description: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    preferredContactMethod: 'phone',
  });

  const [selectedClient, setSelectedClient] = useState(preSelectedClient || null);
  const [jobsites, setJobsites] = useState([]);
  const [loadingJobsites, setLoadingJobsites] = useState(false);
  const [clientContacts, setClientContacts] = useState([]);

  const complaintTypes = [
    'Service Quality',
    'Scheduling Issue',
    'Billing/Invoice',
    'Property Damage',
    'Communication',
    'Staff Behavior',
    'Safety Concern',
    'Other',
  ];

  const priorityLevels = [
    { value: 'low', label: 'Low', color: '#4caf50' },
    { value: 'medium', label: 'Medium', color: '#ff9800' },
    { value: 'high', label: 'High', color: '#f44336' },
    { value: 'urgent', label: 'Urgent', color: '#d32f2f' },
  ];

  useEffect(() => {
    if (preSelectedClient) {
      setSelectedClient(preSelectedClient);
      setFormData(prev => ({ ...prev, clientId: preSelectedClient._id }));
      fetchJobsites(preSelectedClient._id);
      if (preSelectedClient.contacts) {
        setClientContacts(preSelectedClient.contacts);
      }
    }
  }, [preSelectedClient]);

  useEffect(() => {
    if (selectedClient && selectedClient._id !== preSelectedClient?._id) {
      fetchJobsites(selectedClient._id);
      fetchClientContacts(selectedClient._id);
    }
  }, [selectedClient]);

  const fetchJobsites = async (clientId) => {
    try {
      setLoadingJobsites(true);
      const response = await axios.get(`${url}/api/customer/getAllJobSites?id=${clientId}&sitesOnly=true`);
      if (response.data) {
        // Response is an array of jobsites when sitesOnly=true
        setJobsites(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching jobsites:', error);
      setJobsites([]);
    } finally {
      setLoadingJobsites(false);
    }
  };

  const fetchClientContacts = async (clientId) => {
    try {
      const response = await axios.get(`${url}/api/customer/getContacts?id=${clientId}`);
      if (response.data) {
        // Response is an array of contacts
        const contacts = Array.isArray(response.data) ? response.data : [];
        setClientContacts(contacts);
        if (contacts.length > 0) {
          const primaryContact = contacts[0];
          setFormData(prev => ({
            ...prev,
            contactName: primaryContact.name || primaryContact.firstName || '',
            contactPhone: primaryContact.phone || primaryContact.telephone || '',
            contactEmail: primaryContact.email || '',
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching client contacts:', error);
      setClientContacts([]);
    }
  };

  const handleClientChange = (event, value) => {
    setSelectedClient(value);
    setFormData({
      ...formData,
      clientId: value?._id || '',
      jobsiteId: '',
    });
    setJobsites([]);
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleContactSelect = (contact) => {
    setFormData({
      ...formData,
      contactName: contact.name || contact.firstName || `${contact.firstName} ${contact.lastName}` || '',
      contactPhone: contact.phone || contact.telephone || '',
      contactEmail: contact.email || '',
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formData);
  };

  return (
    <form id="complaint-form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Client Selection */}
        {!preSelectedClient && (
          <Grid item xs={12}>
            <Autocomplete
              options={clients}
              getOptionLabel={(option) => 
                option.companyName || `${option.firstName} ${option.lastName}` || option.displayName
              }
              value={selectedClient}
              onChange={handleClientChange}
              disabled={loading || !!preSelectedClient}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Client"
                  variant="outlined"
                  required
                  fullWidth
                />
              )}
            />
          </Grid>
        )}

        {/* Jobsite Selection */}
        {selectedClient && jobsites.length > 0 && (
          <Grid item xs={12}>
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel>Select Jobsite (Optional)</InputLabel>
              <Select
                value={formData.jobsiteId}
                onChange={handleInputChange('jobsiteId')}
                label="Select Jobsite (Optional)"
                disabled={loading || loadingJobsites}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {jobsites.map((jobsite) => (
                  <MenuItem key={jobsite._id} value={jobsite._id}>
                    {jobsite.displayName || jobsite.name || jobsite.address || 'Unnamed Jobsite'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Contact Information Section */}
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Contact Information
          </Typography>
          <div className={classes.contactInfo}>
            {clientContacts.length > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Quick select from existing contacts:
                  </Typography>
                  {clientContacts.map((contact, index) => (
                    <Button
                      key={index}
                      size="small"
                      onClick={() => handleContactSelect(contact)}
                      style={{ marginRight: 8, marginTop: 8 }}
                    >
                      {contact.name || contact.firstName || `${contact.firstName} ${contact.lastName}`}
                    </Button>
                  ))}
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Contact Name"
                  value={formData.contactName}
                  onChange={handleInputChange('contactName')}
                  fullWidth
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Phone Number"
                  value={formData.contactPhone}
                  onChange={handleInputChange('contactPhone')}
                  fullWidth
                  variant="outlined"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.contactEmail}
                  onChange={handleInputChange('contactEmail')}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <Typography variant="body2" color="textSecondary">
                    Preferred Contact Method:
                  </Typography>
                  <RadioGroup
                    row
                    value={formData.preferredContactMethod}
                    onChange={handleInputChange('preferredContactMethod')}
                  >
                    <FormControlLabel value="phone" control={<Radio />} label="Phone" />
                    <FormControlLabel value="email" control={<Radio />} label="Email" />
                    <FormControlLabel value="text" control={<Radio />} label="Text" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </div>
        </Grid>

        {/* Complaint Details Section */}
        <Grid item xs={12}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Complaint Details
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>Complaint Type</InputLabel>
            <Select
              value={formData.complaintType}
              onChange={handleInputChange('complaintType')}
              label="Complaint Type"
              required
            >
              {complaintTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              onChange={handleInputChange('priority')}
              label="Priority"
              required
            >
              {priorityLevels.map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  <span style={{ color: level.color }}>
                    {level.label}
                  </span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Complaint Description"
            multiline
            rows={6}
            value={formData.description}
            onChange={handleInputChange('description')}
            fullWidth
            variant="outlined"
            required
            placeholder="Please provide detailed information about the complaint..."
          />
        </Grid>
      </Grid>
    </form>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  clients: state.clients
});

export default connect(mapStateToProps)(CustomerComplaintForm);